import { UNITS, cellArea, cellToBoundary, cellToLatLng } from "h3-js";
import type { PolicyBuildingsPage, PolicyBuildingsQueryInputs } from "@repo/ping-react-js";
import {
  getHexDiameter,
  getHttpHeaders,
  getPolicyBuildingsCacheArgs,
  getSearchParameters,
  transformData,
} from "@repo/ping-react-js";
import type {
  LatLngType,
  PolicyBuildingType,
  PolicyQueryInputs,
  ReportDataAPIResponse,
} from "@repo/ping-react-js";

import { api } from "services/appSlice";

import {
  OutlineData,
  OutlineDataAPIResponse,
  type ExposedLimitInterpretation,
  type GlobalPolicyBreakdownAPIResponse,
  type PolicyAPIResponse,
  type PolicyBreakdownAPIResponse,
  type PolicyExposedLimitsDetailAPIResponse,
  type ExposedLimitGrouping,
  type ExposedLimitPerilCode,
  type PolicyExposedLimitBreakdownAPIResponse,
  type SettingsAPIResponse,
} from "src/ts-types/ApiTypes";
import {
  WindHistoryAPIResponse,
  WindHistoryByIDAPIResponse,
  TCWindBasinFilter,
  TCWindFootprintData,
} from "src/ts-types/TCWindTypes";
import type {
  HistoricalHurricaneStormsAPIResponse,
  HistoricalHurricaneTrackFeature,
  StormExposure,
} from "src/ts-types/HistoricalHurricaneTrackTypes";
import {
  filterStructuresInParcels,
  parseWKTPolygon,
  type TransformedPolicyAPIResponse,
} from "@repo/ping-react-js";

type PolicyExposedLimitsQueryInputs = Pick<
  PolicyQueryInputs,
  "sovid" | "filters" | "shapeFilter"
> & {
  interpretation: ExposedLimitInterpretation;
  readOnlyToken?: string;
  accessToken?: string;
};

export type PolicyExposedLimitBreakdownQueryInputs =
  PolicyExposedLimitsQueryInputs & {
    // Null breaks down plain TIV per group.
    peril: ExposedLimitPerilCode | null;
    groupBy: ExposedLimitGrouping;
    h3Resolution?: number;
    // Map view for hex/location groups; the footprint is still narrowed by filters and shape.
    bbox?: { lat1: number; lng1: number; lat2: number; lng2: number };
  };

export const mapAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getPolicyBuildings: build.infiniteQuery<PolicyBuildingsPage, PolicyBuildingsQueryInputs, string | null>({
      serializeQueryArgs: ({ queryArgs }) => getPolicyBuildingsCacheArgs(queryArgs),
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (page) => (page.has_remaining && page.next_cursor ? page.next_cursor : undefined),
      },
      query: ({ queryArg, pageParam }) => {
        const params = getSearchParameters(queryArg);
        params.set("page_size", "100");
        if (queryArg.search) params.set("search", queryArg.search);
        if (pageParam) params.set("cursor", pageParam);
        for (const field of queryArg.fields ?? []) params.append("fields", field);
        return {
          url: `/api/v1/pli/policy_buildings?${params}`,
          headers: getHttpHeaders(queryArg.accessToken, queryArg.readOnlyToken),
        };
      },
    }),
    getTCStormHistoryFootprint: build.query<
      TCWindFootprintData & { status_code: number },
      {
        min_lat?: number;
        max_lat?: number;
        min_lon?: number;
        max_lon?: number;
        storm_name?: string;
        storm_year?: string;
        accessToken?: string;
      }
    >({
      query: ({
        min_lat,
        max_lat,
        min_lon,
        max_lon,
        storm_name,
        storm_year,
        accessToken,
      }) => {
        return {
          url: `api/v1/reask/footprint?min_lat=${min_lat}&max_lat=${max_lat}&min_lon=${min_lon}&max_lon=${max_lon}&storm_name=${storm_name}&storm_year=${storm_year}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, null),
        };
      },
    }),
    getTCStormHistoryByBound: build.query<
      WindHistoryAPIResponse,
      {
        min_lat?: number;
        max_lat?: number;
        min_lng?: number;
        max_lng?: number;
        accessToken?: string;
      }
    >({
      query: ({ min_lat, max_lat, min_lng, max_lng, accessToken }) => {
        return {
          url: `api/v1/reask/bounding?min_lat=${min_lat}&max_lat=${max_lat}&min_lon=${min_lng}&max_lng=${max_lng}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, null),
        };
      },
    }),
    getTCStormHistoryById: build.query<
      WindHistoryByIDAPIResponse,
      { storm_id?: string; accessToken?: string }
    >({
      query: ({ storm_id, accessToken }) => {
        return {
          url: `/api/v1/reask?type=item&storm_id=${storm_id}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, null),
        };
      },
    }),
    getTCStormHistory: build.query<
      WindHistoryAPIResponse,
      {
        TCWindBasinFilter?: TCWindBasinFilter;
        TCWindYearFilter?: string[];
        accessToken?: string;
      }
    >({
      query: ({ TCWindBasinFilter, TCWindYearFilter, accessToken }) => {
        const base_url = "/api/v1/reask";

        // Initialize search parameters
        const params = new URLSearchParams();
        params.append("type", "history");

        // Add each basin filter as repeated parameter
        TCWindBasinFilter.forEach((basin) => {
          params.append("TCWindBasinFilter", basin);
        });
        TCWindYearFilter.forEach((year) => {
          params.append("TCWindYearFilter", year);
        });

        // Construct the full URL
        const fullUrl = `${base_url}?${params.toString()}`;

        return {
          url: fullUrl,
          method: "GET",
          headers: getHttpHeaders(accessToken, null),
        };
      },
    }),

    // Static data that only changes when the IBTrACS pipeline reruns, so keep it for the session.
    getHistoricalHurricaneStorms: build.query<
      HistoricalHurricaneStormsAPIResponse,
      { accessToken?: string; readOnlyToken?: string }
    >({
      query: ({ accessToken, readOnlyToken }) => ({
        url: `/api/v1/historical-hurricane-storms`,
        method: "GET",
        headers: getHttpHeaders(accessToken, readOnlyToken),
      }),
      keepUnusedDataFor: 60 * 60,
    }),

    getHistoricalHurricaneStormsInRadius: build.query<
      { sids: string[] },
      { lat: number; lon: number; radiusMiles: number; accessToken?: string; readOnlyToken?: string }
    >({
      query: ({ lat, lon, radiusMiles, accessToken, readOnlyToken }) => {
        const params = new URLSearchParams({ lat: String(lat), lon: String(lon), radius_miles: String(radiusMiles) });
        return {
          url: `/api/v1/historical-hurricane-storms/radius?${params}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),

    getHistoricalHurricaneStormTrack: build.query<
      HistoricalHurricaneTrackFeature,
      { sid: string; accessToken?: string; readOnlyToken?: string }
    >({
      query: ({ sid, accessToken, readOnlyToken }) => ({
        url: `/api/v1/historical-hurricane-storms/${encodeURIComponent(sid)}`,
        method: "GET",
        headers: getHttpHeaders(accessToken, readOnlyToken),
      }),
      keepUnusedDataFor: 60 * 60,
    }),

    // bands: each building counted once, in the strongest wind band it's in, so the three sum to count and tiv.
    getStormExposure: build.query<
      {
        count: number;
        tiv: number;
        avg_radius_miles: number | null;
        bands: Record<"r34" | "r50" | "r64", { count: number; tiv: number }>;
      },
      { sovid: string; sid: string; accessToken?: string; readOnlyToken?: string }
    >({
      query: ({ sovid, sid, accessToken, readOnlyToken }) => {
        const params = new URLSearchParams({ sovid, sid });
        return {
          url: `/api/v1/pli/storm_exposure?${params}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),

    // POST so the sid list rides in the body; as GET params it overran the server's request-line limit.
    getStormExposureBatch: build.query<
      { results: StormExposure[] },
      { sovid: string; sids: string[]; accessToken?: string; readOnlyToken?: string }
    >({
      query: ({ sovid, sids, accessToken, readOnlyToken }) => {
        const params = new URLSearchParams({ sovid });
        return {
          url: `/api/v1/pli/storm_exposure_batch?${params}`,
          method: "POST",
          body: { sids },
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),

    // Which of the given buildings a storm's exposure count includes. Carries the sid so a caller still holding the
    // previous storm's result can tell it apart.
    getStormExposureItemKeys: build.query<
      { sid: string; insideItemKeys: string[] },
      { sovid: string; sid: string; itemKeys: string[]; accessToken?: string; readOnlyToken?: string }
    >({
      query: ({ sovid, sid, itemKeys, accessToken, readOnlyToken }) => {
        const params = new URLSearchParams({ sovid });
        return {
          url: `/api/v1/pli/storm_exposure_item_keys?${params}`,
          method: "POST",
          body: { sid, item_keys: itemKeys },
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
      transformResponse: (response: { item_keys: string[] }, _meta, { sid }) => ({
        sid,
        insideItemKeys: response.item_keys,
      }),
    }),

    getGlobalBreakdown: build.query<
      GlobalPolicyBreakdownAPIResponse,
      { sovid: string; readOnlyToken?: string; accessToken?: string }
    >({
      query: ({ sovid, readOnlyToken, accessToken }) => {
        const params = new URLSearchParams();
        if (sovid) {
          params.set("sovid", sovid);
        }

        return {
          url: `/api/v1/pli/global/policy_breakdown?${params.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getPolicyOverview: build.query<
      any,
      { sovid: string; readOnlyToken?: string; accessToken?: string }
    >({
      query: ({ sovid, readOnlyToken, accessToken }) => {
        const params = new URLSearchParams();
        if (sovid) {
          params.set("sovid", sovid);
        }

        return {
          url: `/api/v1/pli/policy/${sovid}/overview?${params.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    //Where policy breakdown goes
    getPolicyBreakdown: build.query<
      PolicyBreakdownAPIResponse,
      PolicyQueryInputs & {
        fields: string[];
        readOnlyToken?: string;
        accessToken?: string;
        flag?: string;
      }
    >({
      query: ({
        sovid,
        lat1,
        lng1,
        lat2,
        lng2,
        filters,
        attach,
        layerLimit,
        fields,
        shapeFilter,
        readOnlyToken,
        accessToken,
        flag,
      }) => {
        const searchParameters = getSearchParameters({
          sovid,
          lat1,
          lng1,
          lat2,
          lng2,
          filters,
          attach,
          layerLimit,
          shapeFilter,
        });

        if (fields) {
          fields.forEach((f) => searchParameters.append("fields", f));
        }

        if (flag) {
          searchParameters.set("flag", flag);
        }

        return {
          url: `/api/v1/pli/policy_breakdown?${searchParameters.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    downloadPolicyBreakdownCsv: build.mutation<
      any,
      PolicyQueryInputs & {
        fields: string[];
        readOnlyToken?: string;
        accessToken?: string;
      }
    >({
      query: ({
        sovid,
        lat1,
        lng1,
        lat2,
        lng2,
        filters,
        attach,
        layerLimit,
        fields,
        shapeFilter,
        readOnlyToken,
        accessToken,
      }) => {
        const searchParameters = getSearchParameters({
          sovid,
          lat1,
          lng1,
          lat2,
          lng2,
          filters,
          attach,
          layerLimit,
          shapeFilter,
        });

        if (fields) {
          fields.forEach((f) => searchParameters.append("fields", f));
        }

        return {
          url: `/api/v1/pli/policy_breakdown_csv?${searchParameters.toString()}`,
          method: "GET",
          responseHandler(response) {
            return response.blob();
          },
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getPolicy: build.query<
      TransformedPolicyAPIResponse,
      PolicyQueryInputs & {
        readOnlyToken?: string;
        accessToken?: string;
        additional_attrs?: string;
        // The most locations returned as pins; more than that comes back as H3 hexes. 0 forces hexes.
        pointLimit: number;
      }
    >({
      query: ({
        sovid,
        lat1,
        lng1,
        lat2,
        lng2,
        filters,
        attach,
        layerLimit,
        shapeFilter,
        readOnlyToken,
        accessToken,
        additional_attrs,
        pointLimit,
      }) => {
        const searchParameters = getSearchParameters({
          sovid,
          lat1,
          lng1,
          lat2,
          lng2,
          filters,
          attach,
          layerLimit,
          shapeFilter,
        });

        // The policy endpoint returns points when the visible count is <= limit.
        searchParameters.set("limit", pointLimit.toString());

        if (additional_attrs) {
          searchParameters.set("additional_attrs", additional_attrs);
        }

        return {
          url: `/api/v1/pli/policy?${searchParameters.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
      transformResponse: (response: PolicyAPIResponse) => {
        let transformedHexes = null;
        if (response.hexes) {
          transformedHexes = response.hexes.map((h) => {
            const area = cellArea(h?.h3_index, UNITS.km2);
            return {
              ...h,
              center: cellToLatLng(h?.h3_index),
              boundry: cellToBoundary(h?.h3_index)?.map((b) => [b[1], b[0]]),
              area: area,
              limits__total_limit: h?.limits__total_limit,
              h3_index: h?.h3_index,
              diameter: getHexDiameter(area),
            };
          });
        }

        let transformedBuildings = null;
        if (response.buildings) {
          transformedBuildings = response.buildings?.map((b) =>
            transformData(b, response.sync_version || 0),
          );
        }

        return {
          hexes: transformedHexes,
          buildings: transformedBuildings,
        };
      },
    }),
    getStateLayerDataRelevance: build.query<
      ReportDataAPIResponse,
      PolicyQueryInputs & {
        readOnlyToken?: string;
        accessToken?: string;
      }
    >({
      query: ({
        sovid,
        filters,
        attach,
        layerLimit,
        shapeFilter,
        readOnlyToken,
        accessToken,
      }) => {
        const searchParameters = getSearchParameters({
          sovid,
          filters,
          attach,
          layerLimit,
          shapeFilter,
        });
        searchParameters.set("state_breakdown_limit", "100");

        return {
          url: `/api/v1/pli/data_relevance?${searchParameters.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getPolicyExposedLimitsDetail: build.query<
      PolicyExposedLimitsDetailAPIResponse,
      PolicyExposedLimitsQueryInputs
    >({
      query: ({
        sovid,
        filters,
        shapeFilter,
        interpretation,
        readOnlyToken,
        accessToken,
      }) => {
        // No attach/layerLimit: the what-if custom layer would add a limits__total_limit__gt
        // location filter, and exposed limits already apply each policy's own layer terms.
        const searchParameters = getSearchParameters({ sovid, filters, shapeFilter });
        searchParameters.set("interpretation", interpretation);
        searchParameters.set("detail", "true");
        return {
          url: `/api/v1/pli/policy_exposed_limits?${searchParameters.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getPolicyExposedLimitBreakdown: build.query<
      PolicyExposedLimitBreakdownAPIResponse,
      PolicyExposedLimitBreakdownQueryInputs
    >({
      query: ({
        sovid,
        filters,
        shapeFilter,
        interpretation,
        readOnlyToken,
        accessToken,
        peril,
        groupBy,
        h3Resolution,
        bbox,
      }) => {
        // A drawn shape already bounds the request; otherwise the map view does.
        const searchParameters = getSearchParameters({
          sovid,
          filters,
          shapeFilter,
          ...(shapeFilter ? {} : bbox),
        });
        searchParameters.set("interpretation", interpretation);
        if (peril) {
          searchParameters.set("peril", peril);
        }
        searchParameters.set("group_by", groupBy);
        if (h3Resolution !== undefined) {
          searchParameters.set("h3_resolution", String(h3Resolution));
        }
        return {
          url: `/api/v1/pli/policy_exposed_limits_breakdown?${searchParameters.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getPolicyFilters: build.query<
      Record<string, string[]>,
      {
        sovid: string;
        readOnlyToken?: string;
        accessToken?: string;
        fields?: string[];
        total_tiv?: number;
        total_count?: number;
      }
    >({
      query: ({
        sovid,
        readOnlyToken,
        accessToken,
        fields,
        total_tiv,
        total_count,
      }) => {
        const params = new URLSearchParams();
        if (sovid) {
          params.set("sovid", sovid);
        }
        if (fields) {
          fields.forEach((f) => params.append("fields", f));
        }

        if (total_tiv !== undefined) {
          params.set("total_tiv", total_tiv.toFixed(0).toString());
        }

        if (total_count !== undefined) {
          params.set("total_count", total_count.toString());
        }

        return {
          url: `/api/v1/pli/policy_filters?${params.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getBuildingByPolicy: build.query<
      PolicyBuildingType,
      {
        readOnlyToken?: string;
        sov_id: string;
        building_id: string;
        accessToken?: string;
      }
    >({
      query: ({ sov_id, building_id, readOnlyToken, accessToken }) => {
        const params = new URLSearchParams();
        if (sov_id) {
          params.set("sovid", sov_id);
        }

        return {
          url: `/api/v1/pli/policy/${sov_id}/building/${encodeURIComponent(building_id)}?${params.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getLayerSettings: build.query<
      SettingsAPIResponse,
      { readOnlyToken?: string; sovid: string; accessToken?: string }
    >({
      query: ({ readOnlyToken, sovid, accessToken }) => {
        const params = new URLSearchParams();
        if (sovid) {
          params.set("sovid", sovid);
        }

        return {
          url: `/api/v1/pli/settings?${params.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
      transformResponse(response: SettingsAPIResponse): SettingsAPIResponse {
        return {
          ...response,
          pingmaps: {
            ...response.pingmaps,
            integrations: response.pingmaps.integrations,
          },
        };
      },
    }),
    getBuildingOutlines: build.query<
      OutlineData,
      {
        readOnlyToken?: string;
        accessToken?: string;
        sovid: string;
        lat: number;
        lng: number;
      }
    >({
      query: ({ readOnlyToken, sovid, lat, lng, accessToken }) => {
        return {
          url: `/api/v1/pli/building_outlines/${sovid}`,
          method: "POST",
          body: { lat, lng },
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
      transformResponse(response: OutlineDataAPIResponse): OutlineData {
        const parcelLatLngPolygons =
          parseWKTPolygon(response.parcel.lat_lng_lookup) ?? [];
        const parcelIdPolygons =
          parseWKTPolygon(response.parcel.id_lookup) ?? [];

        const adjacentPacelsLatLng =
          response.adjacent_parcels.lat_lng_lookup ?? [];
        const adjacentPacelsId = response.adjacent_parcels.id_lookup ?? [];
        const adjacentParcelLatLngPolygons = adjacentPacelsLatLng.flatMap(
          (latLng) => parseWKTPolygon(latLng) ?? [],
        );
        const adjacentParcelIdPolygons = adjacentPacelsId.flatMap(
          (id) => parseWKTPolygon(id) ?? [],
        );

        const structureLatLngPolygons =
          parseWKTPolygon(response.structure.lat_lng_lookup) ?? [];
        const structureIdPolygons =
          parseWKTPolygon(response.structure.id_lookup) ?? [];

        const useLatLngPolygons =
          parcelLatLngPolygons.length >= parcelIdPolygons.length;
        const primaryParcels = useLatLngPolygons
          ? parcelLatLngPolygons
          : parcelIdPolygons;
        const adjacentParcels = useLatLngPolygons
          ? adjacentParcelLatLngPolygons
          : adjacentParcelIdPolygons;
        const parcels = [...primaryParcels, ...adjacentParcels];

        const primaryParcelPoints: LatLngType[][] = primaryParcels.map(
          (parcel) => parcel.outer,
        );

        const filteredLatLngStructures = filterStructuresInParcels(
          structureLatLngPolygons,
          primaryParcelPoints,
        );
        const filteredIdStructures = filterStructuresInParcels(
          structureIdPolygons,
          primaryParcelPoints,
        );

        const structures =
          filteredLatLngStructures.length >= filteredIdStructures.length
            ? filteredLatLngStructures
            : filteredIdStructures;

        return {
          primaryParcels,
          parcels,
          structures,
        };
      },
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetPolicyBuildingsInfiniteQuery,
  useGetTCStormHistoryFootprintQuery,
  useLazyGetTCStormHistoryFootprintQuery,
  useGetTCStormHistoryByBoundQuery,
  useGetTCStormHistoryByIdQuery,
  useLazyGetTCStormHistoryByIdQuery,
  useGetTCStormHistoryQuery,
  useGetHistoricalHurricaneStormsQuery,
  useGetHistoricalHurricaneStormsInRadiusQuery,
  useGetHistoricalHurricaneStormTrackQuery,
  useGetStormExposureQuery,
  useGetStormExposureBatchQuery,
  useGetStormExposureItemKeysQuery,
  useGetPolicyBreakdownQuery,
  useGetPolicyQuery,
  useGetGlobalBreakdownQuery,
  useGetStateLayerDataRelevanceQuery,
  useGetPolicyExposedLimitsDetailQuery,
  useGetPolicyExposedLimitBreakdownQuery,
  useGetBuildingByPolicyQuery,
  useDownloadPolicyBreakdownCsvMutation,
  useGetLayerSettingsQuery,
  useGetPolicyFiltersQuery,
  useGetBuildingOutlinesQuery,
  useLazyGetBuildingOutlinesQuery,
  useGetPolicyOverviewQuery,
} = mapAPI;
